import React, { useEffect, useState } from 'react'
import { Typography, Link } from '@grammarly/badger-ui'
import { IconButton, InterfaceInfoIcon, Popover, Text } from '@grammarly/design-system'
import { FormattedMessage, useIntl } from 'react-intl'

import { withPublicAssetUrl } from '../../../lib/utils/cdn'
import { useBrowserService } from '../../../lib/hooks'
import { faqSectionIdPrefix } from './utils'

import styles from './ai-detector-shared.module.scss'

const { HS, TL } = Typography

export const OutputDetectedShape = () => (
  <img
    src={`${withPublicAssetUrl('ai-detector/output-detected-shape.svg')}`}
    alt=""
    className={styles.outputDetectedShape}
  />
)

export const OutputPopoverExplanation = () => {
  const intl = useIntl()
  const { isMobileOrTablet } = useBrowserService()
  const [popoverIsOpen, setPopoverIsOpen] = useState(false)
  const [sectionFaqId, setSectionFaqId] = useState<string | undefined>(undefined)

  useEffect(() => {
    const maybeSectionFaqId = document.querySelector(`[id^=${faqSectionIdPrefix}]`)?.id

    if (maybeSectionFaqId && maybeSectionFaqId !== sectionFaqId) {
      setSectionFaqId(maybeSectionFaqId)
    }
  }, [sectionFaqId, setSectionFaqId])

  return (
    <Popover placement="top" dismissOnOutsideClick open={popoverIsOpen} onOpenChange={setPopoverIsOpen}>
      <Popover.Anchor>
        <IconButton
          icon={InterfaceInfoIcon}
          accessibilityLabel={intl.formatMessage({
            id: 'aiDetector.outputExplanationPopoverAnchor.label',
            description: 'AI Detector - Output Explanation Popover Anchor - Label',
            defaultMessage: 'How it works',
          })}
          onClick={() => setPopoverIsOpen(!popoverIsOpen)}
          variant="tertiary"
          tooltipProps={{ open: false }}
        />
      </Popover.Anchor>
      <Popover.Content
        accessibilityLabel={intl.formatMessage({
          id: 'aiDetector.outputExplanationPopoverContent.label',
          description: 'AI Detector - Output Explanation Popover Content - Label',
          defaultMessage:
            'This tool detects AI content by analyzing patterns in the text provided against patterns common to text generated or modified with AI.',
        })}
        title={intl.formatMessage({
          id: 'aiDetector.outputExplanationPopoverContent.title',
          description: 'AI Detector - Output Explanation Popover Content - Title',
          defaultMessage: 'How are results determined?',
        })}
        width={isMobileOrTablet ? 300 : 400}
      >
        <Text variant="text-small" as="p" className={styles.popoverContent}>
          <FormattedMessage
            id="aiDetector.outputExplanationPopoverContent.copy"
            description="AI Detector - Output Explanation Popover Content - Copy"
            defaultMessage="Grammarly's AI detector analyzes your text against a large body of AI-generated content to spot patterns and characteristics typical of text generated with AI.{lineBreak}{lineBreak}To learn more about AI detection, <link>read our FAQs</link>."
            values={{
              lineBreak: () => <br />,
              link: (chunks) => (
                <Link href={`#${sectionFaqId}`} className={styles.faqLink}>
                  {chunks}
                </Link>
              ),
            }}
          />
        </Text>
      </Popover.Content>
    </Popover>
  )
}

export const OutputTitle: React.FC<{ aiPercent: number | null }> = ({ aiPercent }) => {
  const percentString = aiPercent ?? '—'
  return (
    <div>
      <HS as={'h2'} className={styles.outputTitle} data-component-name="output-title">
        <FormattedMessage
          id="aiDetector.outputTitle.heading"
          description="AI Detector - Output Title - Heading"
          defaultMessage="<strong>{percentString}</strong>% of this text appears to be AI-generated"
          values={{
            percentString,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </HS>
      <OutputPopoverExplanation />
    </div>
  )
}

export const OutputAiDetected: React.FC<{ aiPercent: number | null }> = ({ aiPercent }) => {
  const hasAiPercent = aiPercent !== null
  return (
    <div className={styles.outputContent}>
      <TL as="div" className={styles.outputNextSteps}>
        {hasAiPercent && aiPercent > 0 ? (
          <FormattedMessage
            id="aiDetector.outputAiDetected.containsAi"
            description="AI Detector - Output AI Detected - Contains AI"
            defaultMessage="Pinpoint where AI might've been used in this text"
          />
        ) : (
          <FormattedMessage
            id="aiDetector.outputAiDetected.doesNotContainAi"
            description="AI Detector - Output AI Detected - Does Not Contain AI"
            defaultMessage="Go beyond AI detection"
          />
        )}
      </TL>
    </div>
  )
}
